// SafetyPauseBanner — renders on Mode 2 dashboard when a safety event
// has paused the member's subscription. Includes a "why did this happen"
// modal that explains the trigger rule in plain language.
// Spec: Sprint 8 of VOYA9_Frontend_Assessment_Multiprogram_Modes.md
import { useState } from "react";
import { Link } from "react-router-dom";
import Button from "@/components/Button";
import { C } from "@/lib/brand";

export default function SafetyPauseBanner({ pauseEvent }) {
  const [showWhy, setShowWhy] = useState(false);
  if (!pauseEvent) return null;

  const state = pauseEvent.resolutionState || pauseEvent.state || "pending";
  if (state === "pending") return <Pending pauseEvent={pauseEvent} showWhy={showWhy} setShowWhy={setShowWhy} />;
  if (state === "resumed") return <Resolved variant="resumed" pauseEvent={pauseEvent} />;
  if (state === "resumed_with_adjustment") return <Resolved variant="adjusted" pauseEvent={pauseEvent} />;
  if (state === "kept_paused") return <Resolved variant="kept_paused" pauseEvent={pauseEvent} />;
  if (state === "ended_with_refund") return <Resolved variant="ended" pauseEvent={pauseEvent} />;
  return null;
}

function Pending({ pauseEvent, showWhy, setShowWhy }) {
  const consultationId = pauseEvent.consultationId || pauseEvent.consultation?._id;
  const pausedAt = pauseEvent.pausedAt ? relative(pauseEvent.pausedAt) : "just now";
  return (
    <section data-testid="safety-pause-banner" style={{ padding: "14px 24px 0" }}>
      <div
        style={{
          maxWidth: 1100, margin: "0 auto",
          background: C.bone, color: C.ink,
          padding: "28px 24px", borderLeft: `4px solid ${C.coral}`,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.coral }}>
          ★ SAFETY REVIEW IN PROGRESS
        </div>
        <div style={{ marginTop: 10, fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
          Your subscriptions are paused while your physician reviews.
        </div>
        <p style={{ marginTop: 8, fontSize: 14, color: C.warmGray, lineHeight: 1.55 }}>
          No charges during this time.
        </p>
        <div style={{ marginTop: 10, fontSize: 12, color: C.warmGray, letterSpacing: "0.05em" }}>
          ETA: within 24 hours · Paused {pausedAt}
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="safety-pause-why-btn"
            onClick={() => setShowWhy(true)}
            style={{
              background: C.ink, color: C.cream, border: 0,
              padding: "10px 16px", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", cursor: "pointer",
            }}
          >
            READ WHY THIS HAPPENED →
          </button>
          {consultationId && (
            <Link
              to={`/app/care-team/${consultationId}`}
              data-testid="safety-pause-message-link"
              style={{
                padding: "10px 16px", border: `1.5px solid ${C.ink}`,
                color: C.ink, textDecoration: "none",
                fontSize: 11, fontWeight: 800, letterSpacing: "0.18em",
              }}
            >
              MESSAGE PHYSICIAN
            </Link>
          )}
        </div>
      </div>
      {showWhy && <WhyModal pauseEvent={pauseEvent} onClose={() => setShowWhy(false)} />}
    </section>
  );
}

function Resolved({ variant, pauseEvent }) {
  const styles = {
    resumed: { icon: "✓", bg: C.sage, fg: C.sageDark, eyebrow: "YOUR PHYSICIAN HAS RESUMED YOUR PROTOCOL", body: "Your subscription is back to active.", cta: "Read your physician's note →", testId: "safety-resolved-resumed" },
    adjusted: { icon: "✓", bg: C.sage, fg: C.sageDark, eyebrow: "YOUR PROTOCOL HAS BEEN UPDATED", body: "Your subscription is resuming with some changes your physician recommends.", cta: "Review changes →", testId: "safety-resolved-adjusted" },
    kept_paused: { icon: "ⓘ", bg: C.bone, fg: C.ink, eyebrow: "YOUR PROTOCOL REMAINS PAUSED", body: "Your physician recommends keeping your subscription paused for now.", cta: "Read physician's note →", testId: "safety-resolved-kept" },
    ended: { icon: "ⓘ", bg: C.peach, fg: C.peachDark, eyebrow: "YOUR PROTOCOL HAS BEEN ENDED", body: `Your physician has determined the protocol isn't right for you at this time.${pauseEvent.refundDisplay ? ` A refund of ${pauseEvent.refundDisplay} has been processed.` : ""}`, cta: "Read physician's note →", testId: "safety-resolved-ended" },
  };
  const s = styles[variant];
  const consultationId = pauseEvent.consultationId || pauseEvent.consultation?._id;

  return (
    <section data-testid={s.testId} style={{ padding: "14px 24px 0" }}>
      <div
        style={{
          maxWidth: 1100, margin: "0 auto",
          background: s.bg, color: s.fg,
          padding: "24px", display: "flex", gap: 14, alignItems: "flex-start",
        }}
      >
        <span aria-hidden style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{s.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>{s.eyebrow}</div>
          <p style={{ marginTop: 8, fontSize: 14, color: s.fg, opacity: 0.92, lineHeight: 1.55 }}>{s.body}</p>
          {consultationId && (
            <Link
              to={`/app/care-team/${consultationId}`}
              data-testid={`${s.testId}-cta`}
              style={{ display: "inline-block", marginTop: 8, fontSize: 12, fontWeight: 800, letterSpacing: "0.15em", color: s.fg, textDecoration: "underline" }}
            >
              {s.cta}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function WhyModal({ pauseEvent, onClose }) {
  const triggerText = pauseEvent.triggerExplanation || (pauseEvent.triggerRule
    ? `You mentioned ${pauseEvent.triggerRule} on your assessment. Our safety protocol automatically pauses subscriptions when this is reported so your physician can review whether your plan is still safe and appropriate.`
    : "A response on your assessment matched a safety rule that requires physician review before your subscription can continue.");

  return (
    <div
      data-testid="safety-why-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,14,13,0.72)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, backdropFilter: "blur(6px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.cream, color: C.ink, maxWidth: 520, width: "100%",
          padding: "36px 28px 32px", position: "relative", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <button
          type="button"
          data-testid="safety-why-close"
          onClick={onClose}
          aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: C.warmGray }}
        >
          ✕
        </button>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.coral }}>★ WHY THIS HAPPENED</div>
        <h2 style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
          Here's what triggered this review.
        </h2>
        <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.65, color: C.ink }}>
          {triggerText}
        </p>
        <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6, color: C.warmGray }}>
          Your physician will review your case within 24 hours and either resume, adjust, or end your protocol. You won't be charged while paused.
        </p>
        <div style={{ marginTop: 22 }}>
          <Button variant="primary" block testId="safety-why-ack" onClick={onClose}>
            GOT IT
          </Button>
        </div>
      </div>
    </div>
  );
}

function relative(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}
